
export const EPSILON = 1e-9;
export const EARTH_RADIUS_M = 6371000;

export const toRadians = (value: number) => (value * Math.PI) / 180;

export const pointInBounds = (lng: number, lat: number, minLng: number, minLat: number, maxLng: number, maxLat: number) =>
  lng >= minLng - EPSILON && lng <= maxLng + EPSILON && lat >= minLat - EPSILON && lat <= maxLat + EPSILON;

export const onSegment = (ax: number, ay: number, bx: number, by: number, cx: number, cy: number) =>
  Math.min(ax, bx) - EPSILON <= cx && cx <= Math.max(ax, bx) + EPSILON &&
  Math.min(ay, by) - EPSILON <= cy && cy <= Math.max(ay, by) + EPSILON;

export const orientation = (ax: number, ay: number, bx: number, by: number, cx: number, cy: number) => {
  const val = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
  if (Math.abs(val) < EPSILON) return 0;
  return val > 0 ? 1 : -1;
};

export const segmentsIntersect = (ax: number, ay: number, bx: number, by: number, cx: number, cy: number, dx: number, dy: number) => {
  const o1 = orientation(ax, ay, bx, by, cx, cy);
  const o2 = orientation(ax, ay, bx, by, dx, dy);
  const o3 = orientation(cx, cy, dx, dy, ax, ay);
  const o4 = orientation(cx, cy, dx, dy, bx, by);

  if (o1 === 0 && onSegment(ax, ay, bx, by, cx, cy)) return true;
  if (o2 === 0 && onSegment(ax, ay, bx, by, dx, dy)) return true;
  if (o3 === 0 && onSegment(cx, cy, dx, dy, ax, ay)) return true;
  if (o4 === 0 && onSegment(cx, cy, dx, dy, bx, by)) return true;

  return (o1 > 0) !== (o2 > 0) && (o3 > 0) !== (o4 > 0);
};

export const countVertices = (geometry: any): number => {
  if (!geometry) return 0;
  const { type, coordinates, geometries } = geometry;
  switch (type) {
    case 'Point':
      return 1;
    case 'MultiPoint':
    case 'LineString':
      return Array.isArray(coordinates) ? coordinates.length : 0;
    case 'MultiLineString':
      return Array.isArray(coordinates)
        ? coordinates.reduce((sum: number, line: any) => sum + (Array.isArray(line) ? line.length : 0), 0)
        : 0;
    case 'Polygon':
      return Array.isArray(coordinates)
        ? coordinates.reduce((sum: number, ring: any) => sum + (Array.isArray(ring) ? ring.length : 0), 0)
        : 0;
    case 'MultiPolygon':
      return Array.isArray(coordinates)
        ? coordinates.reduce(
            (sum: number, poly: any) =>
              sum +
              (Array.isArray(poly)
                ? poly.reduce((s: number, ring: any) => s + (Array.isArray(ring) ? ring.length : 0), 0)
                : 0),
            0
          )
        : 0;
    case 'GeometryCollection':
      return Array.isArray(geometries)
        ? geometries.reduce((sum: number, geom: any) => sum + countVertices(geom), 0)
        : 0;
    default:
      return 0;
  }
};

export const haversineDistanceKm = (lng1: number, lat1: number, lng2: number, lat2: number) => {
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const rLat1 = toRadians(lat1);
  const rLat2 = toRadians(lat2);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(rLat1) * Math.cos(rLat2) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return (EARTH_RADIUS_M * c) / 1000;
};

export const lineLengthKm = (geometry: any): number => {
  const sumSegments = (coords: any[]): number => {
    if (!Array.isArray(coords)) return 0;
    let total = 0;
    for (let i = 0; i < coords.length - 1; i += 1) {
      const [lng1, lat1] = coords[i];
      const [lng2, lat2] = coords[i + 1];
      if ([lng1, lat1, lng2, lat2].every(Number.isFinite)) {
        total += haversineDistanceKm(lng1, lat1, lng2, lat2);
      }
    }
    return total;
  };

  if (!geometry) return 0;
  if (geometry.type === 'LineString') {
    return sumSegments(geometry.coordinates || []);
  }
  if (geometry.type === 'MultiLineString') {
    return (geometry.coordinates || []).reduce((sum: number, line: any) => sum + sumSegments(line || []), 0);
  }
  return 0;
};

export const polygonAreaSqM = (geometry: any): number => {
  const ringArea = (ring: any[]): number => {
    if (!Array.isArray(ring) || ring.length < 3) return 0;
    const refLat = ring.reduce((sum, coord) => sum + (coord?.[1] || 0), 0) / ring.length;
    const cosRef = Math.cos(toRadians(refLat));
    let area = 0;
    for (let i = 0; i < ring.length; i += 1) {
      const [lng1, lat1] = ring[i];
      const [lng2, lat2] = ring[(i + 1) % ring.length];
      const x1 = toRadians(lng1) * EARTH_RADIUS_M * cosRef;
      const y1 = toRadians(lat1) * EARTH_RADIUS_M;
      const x2 = toRadians(lng2) * EARTH_RADIUS_M * cosRef;
      const y2 = toRadians(lat2) * EARTH_RADIUS_M;
      area += x1 * y2 - x2 * y1;
    }
    return Math.abs(area) / 2;
  };

  if (!geometry) return 0;
  if (geometry.type === 'Polygon') {
    return ringArea(geometry.coordinates?.[0] || []);
  }
  if (geometry.type === 'MultiPolygon') {
    return (geometry.coordinates || []).reduce((sum: number, poly: any) => sum + ringArea(poly?.[0] || []), 0);
  }
  return 0;
};

export const polygonSpanKm = (geometry: any): number => {
  const collect = (coords: any[]): { minLng: number; maxLng: number; minLat: number; maxLat: number } => {
    let minLng = Infinity;
    let maxLng = -Infinity;
    let minLat = Infinity;
    let maxLat = -Infinity;
    (coords || []).forEach((coord: any) => {
      if (Array.isArray(coord[0])) {
        const child = collect(coord);
        minLng = Math.min(minLng, child.minLng);
        maxLng = Math.max(maxLng, child.maxLng);
        minLat = Math.min(minLat, child.minLat);
        maxLat = Math.max(maxLat, child.maxLat);
      } else {
        const [lng, lat] = coord;
        if (Number.isFinite(lng) && Number.isFinite(lat)) {
          minLng = Math.min(minLng, lng);
          maxLng = Math.max(maxLng, lng);
          minLat = Math.min(minLat, lat);
          maxLat = Math.max(maxLat, lat);
        }
      }
    });
    return { minLng, maxLng, minLat, maxLat };
  };

  if (!geometry) return 0;
  const { minLng, maxLng, minLat, maxLat } = collect(geometry.coordinates || []);
  if (!Number.isFinite(minLng) || !Number.isFinite(maxLng) || !Number.isFinite(minLat) || !Number.isFinite(maxLat)) {
    return 0;
  }
  const midLat = (minLat + maxLat) / 2;
  const midLng = (minLng + maxLng) / 2;
  const widthKm = haversineDistanceKm(minLng, midLat, maxLng, midLat);
  const heightKm = haversineDistanceKm(midLng, minLat, midLng, maxLat);
  return Math.max(widthKm, heightKm);
};

export const segmentIntersectsRect = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  minLng: number,
  minLat: number,
  maxLng: number,
  maxLat: number
) => {
  const segMinLng = Math.min(x1, x2);
  const segMaxLng = Math.max(x1, x2);
  const segMinLat = Math.min(y1, y2);
  const segMaxLat = Math.max(y1, y2);

  if (
    segMaxLng < minLng - EPSILON ||
    segMinLng > maxLng + EPSILON ||
    segMaxLat < minLat - EPSILON ||
    segMinLat > maxLat + EPSILON
  ) {
    return false;
  }

  const edges: [number, number, number, number][] = [
    [minLng, minLat, maxLng, minLat],
    [maxLng, minLat, maxLng, maxLat],
    [maxLng, maxLat, minLng, maxLat],
    [minLng, maxLat, minLng, minLat]
  ];

  return edges.some(([ex1, ey1, ex2, ey2]) => segmentsIntersect(x1, y1, x2, y2, ex1, ey1, ex2, ey2));
};

export const lineIntersectsBounds = (
  coordinates: any,
  minLng: number,
  minLat: number,
  maxLng: number,
  maxLat: number
) => {
  if (!Array.isArray(coordinates)) return false;
  for (let i = 0; i < coordinates.length - 1; i += 1) {
    const start = coordinates[i];
    const end = coordinates[i + 1];
    if (!Array.isArray(start) || !Array.isArray(end)) continue;
    if (start.length < 2 || end.length < 2) continue;
    const [lng1, lat1] = start;
    const [lng2, lat2] = end;
    if (!Number.isFinite(lng1) || !Number.isFinite(lat1) || !Number.isFinite(lng2) || !Number.isFinite(lat2)) continue;

    if (pointInBounds(lng1, lat1, minLng, minLat, maxLng, maxLat) || pointInBounds(lng2, lat2, minLng, minLat, maxLng, maxLat)) {
      return true;
    }

    if (segmentIntersectsRect(lng1, lat1, lng2, lat2, minLng, minLat, maxLng, maxLat)) {
      return true;
    }
  }
  return false;
};

export const pointInRing = (lng: number, lat: number, ring: any[]): boolean => {
  if (!Array.isArray(ring) || ring.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const a = ring[i];
    const b = ring[j];
    if (!Array.isArray(a) || !Array.isArray(b) || a.length < 2 || b.length < 2) continue;
    const [ax, ay] = a;
    const [bx, by] = b;
    const intersects = (ay > lat) !== (by > lat) && (lng < ((bx - ax) * (lat - ay)) / (by - ay + EPSILON) + ax);
    if (intersects) inside = !inside;
  }
  return inside;
};

export const pointInPolygonWithHoles = (lng: number, lat: number, rings: any[]): boolean => {
  if (!Array.isArray(rings) || !rings.length) return false;
  const outer = rings[0];
  if (!pointInRing(lng, lat, outer)) return false;
  for (let i = 1; i < rings.length; i += 1) {
    if (pointInRing(lng, lat, rings[i])) return false;
  }
  return true;
};

export const polygonIntersectsBounds = (
  coordinates: any,
  minLng: number,
  minLat: number,
  maxLng: number,
  maxLat: number
) => {
  if (!Array.isArray(coordinates) || !coordinates.length) return false;
  const viewportCorners: [number, number][] = [
    [minLng, minLat],
    [maxLng, minLat],
    [maxLng, maxLat],
    [minLng, maxLat]
  ];

  const checkRing = (ring: any[]): boolean => {
    if (!Array.isArray(ring) || ring.length < 2) return false;
    for (let i = 0; i < ring.length; i += 1) {
      const coord = ring[i];
      if (!Array.isArray(coord) || coord.length < 2) continue;
      const [lng, lat] = coord;
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
      if (pointInBounds(lng, lat, minLng, minLat, maxLng, maxLat)) {
        return true;
      }
    }

    return lineIntersectsBounds(ring, minLng, minLat, maxLng, maxLat);
  };

  // Edge intersection or vertex inclusion
  if (coordinates.some((ring: any) => checkRing(ring))) {
    return true;
  }

  // Check if viewport is completely inside polygon
  if (pointInPolygonWithHoles(viewportCorners[0][0], viewportCorners[0][1], coordinates) ||
      pointInPolygonWithHoles(viewportCorners[1][0], viewportCorners[1][1], coordinates) ||
      pointInPolygonWithHoles(viewportCorners[2][0], viewportCorners[2][1], coordinates) ||
      pointInPolygonWithHoles(viewportCorners[3][0], viewportCorners[3][1], coordinates)) {
    return true;
  }

  return false;
};

export const computeDrawingBounds = (drawing: any) => {
  try {
    const geometry = JSON.parse(drawing.geometry);
    let minLat = Number.POSITIVE_INFINITY;
    let maxLat = Number.NEGATIVE_INFINITY;
    let minLng = Number.POSITIVE_INFINITY;
    let maxLng = Number.NEGATIVE_INFINITY;

    const pushCoord = (lng: number, lat: number) => {
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
      }
    };

    if (drawing.type === 'POINT' && geometry?.type === 'Point' && Array.isArray(geometry.coordinates)) {
      const [lng, lat] = geometry.coordinates;
      pushCoord(lng, lat);
    } else if (drawing.type === 'LINE' && geometry?.type === 'LineString' && Array.isArray(geometry.coordinates)) {
      geometry.coordinates.forEach((coord: any) => {
        if (Array.isArray(coord) && coord.length >= 2) {
          pushCoord(coord[0], coord[1]);
        }
      });
    } else if (drawing.type === 'POLYGON' && geometry?.type === 'Polygon' && Array.isArray(geometry.coordinates)) {
      const ring = geometry.coordinates[0];
      if (Array.isArray(ring)) {
        ring.forEach((coord: any) => {
          if (Array.isArray(coord) && coord.length >= 2) {
            pushCoord(coord[0], coord[1]);
          }
        });
      }
    } else if (geometry?.type === 'MultiPolygon' && Array.isArray(geometry.coordinates)) {
      geometry.coordinates.forEach((poly: any) => {
        const ring = Array.isArray(poly) ? poly[0] : null;
        if (Array.isArray(ring)) {
          ring.forEach((coord: any) => {
            if (Array.isArray(coord) && coord.length >= 2) {
              pushCoord(coord[0], coord[1]);
            }
          });
        }
      });
    } else if (geometry?.type === 'MultiLineString' && Array.isArray(geometry.coordinates)) {
      geometry.coordinates.forEach((line: any) => {
        if (Array.isArray(line)) {
          line.forEach((coord: any) => {
            if (Array.isArray(coord) && coord.length >= 2) {
              pushCoord(coord[0], coord[1]);
            }
          });
        }
      });
    }

    if (!Number.isFinite(minLat) || !Number.isFinite(maxLat) || !Number.isFinite(minLng) || !Number.isFinite(maxLng)) {
      return null;
    }

    return { minLat, maxLat, minLng, maxLng };
  } catch {
    return null;
  }
};
